/**
 * Market Card — Figma "card" state, dark mode.
 *
 * Two outcome layouts driven by `isMultiChoice(marketEvent)`:
 *   • Binary (single-choice): YES/NO rows with check/X glyph icons.
 *   • Multi-choice: up to 3 contender rows with their market images.
 *
 * The compliance footer link toggles a sub-view rendered inside the same
 * container chrome (regulatory text). The chevron control conditionally
 * returns to the main card when in info, or minimises to the preview.
 *
 * Vertical rhythm matches Figma exactly:
 *   BrandHeader  → ContentBlock : 20px
 *   ContentBlock → OutcomeList  : 24px
 *   OutcomeList  → CTA          : 32px
 *   CTA          → Footer       : 16px (+ Footer's own pt-2 = 24px to logo)
 * Implemented via per-section margin-top so each gap is independently tuned;
 * a uniform root `gap-*` couldn't span all four values.
 */

import type { JSX } from "preact";
import { Fragment } from "preact";
import { useEffect, useState } from "preact/hooks";
import type { IMarketDTO } from "@repo/types/market";
import type { IMarketEventDTO } from "../api";
import { getConfig, webAppUrl } from "../config";
import { useAnimationVisibility, useTimeLeft } from "../hooks";
import { messages } from "../i18n";
import {
  cardSurfaceShellClasses,
  floatingPositionClassByPosition,
  surfaceAnimationClass,
  surfaceClickTargetClass,
  WidgetPosition,
  widgetSurfaceWidthClass,
} from "../utils/constants";
import {
  formatPriceChange,
  formatVolumeShort,
  type TimeLeft,
} from "../utils/formatters";
import {
  BinaryOutcome,
  getMarketImageSrc,
  getMarketPriceChange24h,
  getMarketProbability,
  getMarketUrl,
  getMarketVolume,
  getTopContenders,
  isMultiChoice,
  type GetMarketUrlOptions,
} from "../utils/market-event-helpers";
import { AdvertisementLabel } from "./advertisement-label";
import { CloseIcon } from "./close-icon";
import { MarketImage } from "./market-image";
import {
  ArrowOutwardIcon,
  CheckIcon,
  ChevronDownIcon,
  InfoIcon,
  ScheduleIcon,
} from "./material-icons";
import { OutcomeRow } from "./outcome-row";
import { PredictorBrandIcon } from "./predictor-brand-icon";
import { PredictorFullLogo } from "./predictor-full-logo";

const titleId = "pw-card-title";

// The dynamic position + width classes are prepended at the call site since
// they depend on `getConfig().position`, which only resolves once the
// widget has booted. The shell chrome is shared with the loading skeleton.
const containerClasses = cardSurfaceShellClasses;

export interface MarketCardProps {
  marketEvent: IMarketEventDTO;
  visible: boolean;
  onClose: () => void;
}

const CardView = {
  Main: "main",
  Info: "info",
} as const;
type CardView = (typeof CardView)[keyof typeof CardView];

export function MarketCard({
  marketEvent,
  visible,
  onClose,
}: MarketCardProps): JSX.Element | null {
  const { shouldRender, isExiting } = useAnimationVisibility(visible);
  const [view, setView] = useState<CardView>(CardView.Main);

  // Reset to main when a new market event resolves so a stale info view
  // doesn't carry over across navigations.
  useEffect(() => {
    setView(CardView.Main);
  }, [marketEvent.eventTicker]);

  if (!shouldRender) return null;

  const position = getConfig().position;
  const animationClass = surfaceAnimationClass(position, isExiting);
  const widthClass = widgetSurfaceWidthClass(position, "card");
  // While exiting inline, overlay absolutely so the transition stage measures
  // only the incoming surface (no stack-and-jump) — see `InlineStage`.
  const positionClass =
    position === WidgetPosition.Inline && isExiting
      ? "pw:absolute pw:inset-x-0 pw:top-0 pw:mx-auto"
      : floatingPositionClassByPosition[position];

  const handleClose = (): void => {
    if (view === CardView.Info) {
      setView(CardView.Main);
      return;
    }
    // `onClose` flips `visible` on the parent; `useAnimationVisibility`
    // already handles the exit-animation → unmount sequence. Wrapping in
    // setTimeout(..., exitAnimationDuration) double-counts the delay and
    // adds dead time before the animation even starts.
    onClose();
  };

  const toggleInfo = (): void => {
    setView(view === CardView.Info ? CardView.Main : CardView.Info);
  };

  const hideWhenInfo: JSX.HTMLAttributes<HTMLDivElement>["style"] = {
    visibility: view === CardView.Info ? "hidden" : "visible",
  };

  return (
    // The dialog role sits on the outer wrapper rather than the card surface so
    // the advertisement disclosure is inside the dialog subtree — screen readers
    // can restrict navigation to that subtree and would otherwise skip it.
    <div
      aria-labelledby={titleId}
      aria-modal="true"
      className={`${positionClass} ${widthClass} ${animationClass} pw:font-sans`}
      role="dialog"
    >
      <AdvertisementLabel />

      <div
        className={`${containerClasses} ${surfaceClickTargetClass} pw:w-full`}
      >
        <BrandHeader onClose={handleClose} />

        {/* Middle slot. Main content stays in flow (always rendered, hidden
            via `visibility` when in info mode) so the card height is dictated
            by the main view in either mode. Info content is overlaid on top
            via absolute positioning, covering ContentBlock through CTA. */}
        <div className="pw:relative pw:w-full pw:mt-5">
          <div style={hideWhenInfo}>
            <ContentBlock marketEvent={marketEvent} />
          </div>
          <div className="pw:mt-6" style={hideWhenInfo}>
            <OutcomeList marketEvent={marketEvent} />
          </div>
          <div className="pw:mt-8" style={hideWhenInfo}>
            <PlacePredictionCTA marketEvent={marketEvent} />
          </div>
          {view === CardView.Info ? (
            <div className="pw:absolute pw:inset-0">
              <InfoView />
            </div>
          ) : null}
        </div>

        <Footer onComplianceClick={toggleInfo} />
        <AdvertisementSource />
      </div>
    </div>
  );
}

/**
 * Closing disclosure naming where the advertised information comes from. Sits
 * below every other element in the card, including the brand/compliance footer.
 * 13px / 16px, no tracking, centred, with only the brand in bold.
 *
 * No bottom margin: the card's own `p-4` supplies the 16px to its edge, and a
 * margin here would stack on top of that.
 */
function AdvertisementSource(): JSX.Element {
  const s = messages().card.advertisementSource;
  return (
    <p className="pw:mt-4 pw:w-full pw:text-center pw:text-[0.8125rem] pw:font-normal pw:leading-4">
      {s.lead}
      <strong className="pw:font-bold">{s.bold}</strong>
      {s.rest}
    </p>
  );
}

function BrandHeader({ onClose }: { onClose: () => void }): JSX.Element {
  const m = messages();
  return (
    <div className="pw:flex pw:items-center pw:justify-between pw:w-full">
      <div className="pw:flex pw:items-center pw:gap-4">
        <PredictorBrandIcon className="pw:h-7 pw:w-7" />
        <span className="pw:text-[0.9375rem] pw:font-medium pw:leading-5">
          {m.card.brand}
        </span>
      </div>
      {/* A down chevron rather than an X: the control minimises the card back
          to the preview, it does not dismiss the widget. Behaviour is
          unchanged — only the glyph and its label. */}
      <button
        aria-label={m.card.minimiseAriaLabel}
        className="pw:flex pw:items-center pw:justify-center pw:h-6 pw:w-6 pw:rounded-full pw:bg-type-muted-foreground pw:cursor-pointer pw:text-type-primary-foreground"
        onClick={onClose}
        type="button"
      >
        <ChevronDownIcon className="pw:h-6 pw:w-6" />
      </button>
    </div>
  );
}

function Footer({
  onComplianceClick,
}: {
  onComplianceClick: () => void;
}): JSX.Element {
  const m = messages();
  return (
    <div className="pw:flex pw:items-center pw:justify-between pw:w-full pw:mt-4 pw:pt-2">
      <a
        aria-label="Predictor home"
        className="pw:flex pw:items-center pw:cursor-pointer"
        href={webAppUrl}
        rel="noopener noreferrer"
        target="_blank"
      >
        <PredictorFullLogo className="pw:h-[1.6rem] pw:w-[8.8125rem]" />
      </a>
      <button
        aria-label={m.card.compliance.shortLabel}
        className="pw:flex pw:items-center pw:justify-center pw:gap-1 pw:h-7 pw:pl-4 pw:pr-2 pw:rounded-full pw:cursor-pointer pw:text-type-primary-foreground"
        onClick={onComplianceClick}
        type="button"
      >
        <span className="pw:text-[0.6875rem] pw:font-normal pw:leading-4 pw:tracking-[0.003em]">
          {m.card.compliance.shortLabel}
        </span>
        <InfoIcon className="pw:h-[1.125rem] pw:w-[1.125rem]" />
      </button>
    </div>
  );
}

function ContentBlock({
  marketEvent,
}: {
  marketEvent: IMarketEventDTO;
}): JSX.Element {
  const timeLeft = useTimeLeft(marketEvent.closesAt);

  return (
    <div className="pw:flex pw:flex-col pw:gap-4 pw:w-full">
      <div className="pw:flex pw:items-center pw:gap-2 pw:w-full">
        <div className="pw:h-[3.625rem] pw:w-[3.625rem] pw:flex-shrink-0 pw:rounded-2xl pw:overflow-hidden pw:bg-type-muted-foreground">
          <MarketImage
            className="pw:h-full pw:w-full pw:object-cover"
            src={getMarketImageSrc(marketEvent)}
          />
        </div>
        <h2
          className="pw:flex-1 pw:min-w-0 pw:text-[1.0625rem] pw:font-bold pw:leading-6 pw:line-clamp-2"
          id={titleId}
        >
          {marketEvent.title}
        </h2>
      </div>
      <TimeLeftPill timeLeft={timeLeft} />
    </div>
  );
}

function TimeLeftPill({
  timeLeft,
}: {
  timeLeft: TimeLeft | null;
}): JSX.Element | null {
  if (!timeLeft) return null;
  const m = messages();
  const value =
    timeLeft.kind === "days"
      ? (timeLeft.count === 1
          ? m.card.timeLeftDay
          : m.card.timeLeftDays
        ).replace("{count}", String(timeLeft.count))
      : timeLeft.text;

  return (
    <div className="pw:flex pw:items-center pw:gap-1 pw:h-6 pw:pl-1 pw:pr-3 pw:rounded-3xl pw:bg-widget-overlay pw:self-start">
      <ScheduleIcon className="pw:h-5 pw:w-5" />
      <span className="pw:text-[0.8125rem] pw:font-medium pw:leading-4 pw:tabular-nums">
        {m.card.timeLeftLabel.replace("{value}", value)}
      </span>
    </div>
  );
}

function OutcomeList({
  marketEvent,
}: {
  marketEvent: IMarketEventDTO;
}): JSX.Element {
  const m = messages();
  const { exploreMarketsUrl, apiKey } = getConfig();
  // Each row deep-links its own outcome — clicking YES lands with YES
  // pre-selected in the trading modal; clicking a contender lands with
  // that contender pre-selected. "See all options" stays neutral.
  const openWith =
    (options: GetMarketUrlOptions = {}) =>
    (): void => {
      window.open(
        getMarketUrl(marketEvent, exploreMarketsUrl, {
          ...options,
          partnerKey: apiKey,
        }),
        "_blank",
        "noopener,noreferrer",
      );
    };
  const seeAllUrl = getMarketUrl(marketEvent, exploreMarketsUrl, {
    partnerKey: apiKey,
  });

  if (isMultiChoice(marketEvent)) {
    const contenders = getTopContenders(marketEvent);
    return (
      <div className="pw:flex pw:flex-col pw:gap-3 pw:w-full">
        {contenders.map((contender, idx) => (
          <Fragment key={contender.id ?? contender.slug}>
            {idx > 0 ? <RowDivider /> : null}
            <ContenderRow
              contender={contender}
              onClick={openWith({
                market: contender,
                side: BinaryOutcome.Yes,
              })}
            />
          </Fragment>
        ))}
        <a
          className="pw:flex pw:items-center pw:gap-1 pw:cursor-pointer pw:self-start pw:text-type-primary-foreground pw:mt-1"
          href={seeAllUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          <span className="pw:text-[0.9375rem] pw:font-medium pw:leading-5">
            {m.card.seeAllOptions}
          </span>
          <ArrowOutwardIcon className="pw:h-5 pw:w-5" />
        </a>
      </div>
    );
  }

  return (
    <div className="pw:flex pw:flex-col pw:gap-3 pw:w-full">
      <BinaryRow
        marketEvent={marketEvent}
        onClick={openWith({ side: BinaryOutcome.Yes })}
        outcome={BinaryOutcome.Yes}
      />
      <RowDivider />
      <BinaryRow
        marketEvent={marketEvent}
        onClick={openWith({ side: BinaryOutcome.No })}
        outcome={BinaryOutcome.No}
      />
    </div>
  );
}

function BinaryRow({
  marketEvent,
  outcome,
  onClick,
}: {
  marketEvent: IMarketEventDTO;
  outcome: BinaryOutcome;
  onClick: () => void;
}): JSX.Element {
  const m = messages();
  const primary = marketEvent.markets[0];
  const isYes = outcome === BinaryOutcome.Yes;
  const rawPrice = isYes
    ? primary?.currentPrice?.YES
    : primary?.currentPrice?.NO;
  const probability = rawPrice ? Math.round(parseFloat(rawPrice) * 100) : 0;
  const volume = primary ? getMarketVolume(primary) : 0;
  const change = formatPriceChange(
    primary ? getMarketPriceChange24h(primary) : 0,
  );
  const icon = isYes ? (
    <div className="pw:flex pw:items-center pw:justify-center pw:w-full pw:h-full pw:bg-outcome-yes pw:text-outcome-yes-foreground">
      <CheckIcon className="pw:h-6 pw:w-6" />
    </div>
  ) : (
    <div className="pw:flex pw:items-center pw:justify-center pw:w-full pw:h-full pw:bg-outcome-no pw:text-outcome-no-foreground">
      <CloseIcon className="pw:h-6 pw:w-6" />
    </div>
  );

  return (
    <OutcomeRow
      ariaLabel={`${m.outcomes[outcome]} ${probability}% — open market details`}
      icon={icon}
      onClick={onClick}
      priceChangeDirection={change.direction}
      priceChangeText={change.text}
      probability={probability}
      title={m.outcomes[outcome]}
      volumeText={formatVolumeShort(volume)}
    />
  );
}

function ContenderRow({
  contender,
  onClick,
}: {
  contender: IMarketDTO;
  onClick: () => void;
}): JSX.Element {
  const change = formatPriceChange(getMarketPriceChange24h(contender));
  // Per-market resolution: see `getMarketImageSrc` for why a contender usually
  // lands on its raw imageUrl today (pairing is event-level).
  const icon = (
    <MarketImage
      className="pw:h-full pw:w-full pw:object-cover"
      src={getMarketImageSrc(contender)}
    />
  );

  return (
    <OutcomeRow
      ariaLabel={`${contender.title} ${getMarketProbability(contender)}% — open market details`}
      icon={icon}
      onClick={onClick}
      priceChangeDirection={change.direction}
      priceChangeText={change.text}
      probability={getMarketProbability(contender)}
      title={contender.title}
      volumeText={formatVolumeShort(getMarketVolume(contender))}
    />
  );
}

function RowDivider(): JSX.Element {
  return (
    <div
      aria-hidden="true"
      className="pw:h-px pw:w-[calc(100%-3.25rem)] pw:rounded-full pw:bg-widget-overlay pw:self-end"
    />
  );
}

// No `capitalize` on the button: it title-cased the old "Place A Prediction"
// but would render the domain in the new label as "Predictor.Io".
function PlacePredictionCTA({
  marketEvent,
}: {
  marketEvent: IMarketEventDTO;
}): JSX.Element {
  const m = messages();
  const { exploreMarketsUrl, apiKey } = getConfig();
  const url = getMarketUrl(marketEvent, exploreMarketsUrl, {
    partnerKey: apiKey,
  });
  const open = (): void => {
    window.open(url, "_blank", "noopener,noreferrer");
  };
  return (
    <button
      className="pw:flex pw:items-center pw:justify-center pw:gap-2 pw:h-10 pw:w-full pw:rounded-full pw:bg-button-secondary pw:text-button-secondary-foreground pw:cursor-pointer"
      onClick={open}
      type="button"
    >
      <span className="pw:text-[1.0625rem] pw:font-medium pw:leading-[1.375rem] pw:tracking-[-0.0125em]">
        {m.card.ctaPredict}
      </span>
      <ArrowOutwardIcon className="pw:h-6 pw:w-6" />
    </button>
  );
}

function InfoView(): JSX.Element {
  const m = messages();
  const c = m.card.compliance;
  return (
    <div className="pw:flex pw:flex-col pw:gap-4 pw:w-full">
      <div className="pw:flex pw:flex-col pw:gap-4 pw:w-full">
        <h2 className="pw:text-[1.0625rem] pw:font-bold pw:leading-6">
          {c.title}
        </h2>
        {/* Divider matches Figma's `h-0` + absolute-positioned line pattern:
            the layout container takes ZERO height, the 1px line is painted
            at the bottom edge via absolute positioning. Keeps the divider
            visually exact while not pushing the body content down by 1px,
            which would compound through the rest of the surface.
            Color: `widget-overlay` (rgba(0,0,0,0.7)) composites to ~91%
            black on top of the card's 70%-black backplate, staying visible
            regardless of partner-page background — `widget-border` (#464547)
            blends into a light-page-tinted backplate. */}
        <div className="pw:relative pw:h-0 pw:w-full" aria-hidden="true">
          <div className="pw:absolute pw:left-0 pw:right-0 pw:bottom-0 pw:h-px pw:bg-widget-overlay" />
        </div>
      </div>
      <div className="pw:flex pw:flex-col pw:gap-5 pw:text-[0.9375rem] pw:font-medium pw:leading-5">
        <p>
          {c.paragraph1Lead}
          <strong className="pw:font-bold">{c.paragraph1Bold}</strong>
          {c.paragraph1Rest}
        </p>
        <p>
          <strong className="pw:font-bold">{c.paragraph2Bold}</strong>
          {c.paragraph2Rest}
        </p>
        <p>
          <strong className="pw:font-bold">{c.paragraph3Bold}</strong>
          {c.paragraph3Rest}
        </p>
      </div>
    </div>
  );
}
